/**
 * @fileoverview Builder-shell illustration: the full canvas as the user sees it (Phase 5, J3).
 * @module ui-admin/help/illustrations/IllustrationBuilderShell
 *
 * Responsibilities: - Faithfully recreate the Phase 2 builder shell (header bar with Body/Cover
 *                     Page tabs + Add component, left configuration panel, centre canvas with a
 *                     sample band card, and the right preview pane) with numbered callouts.
 * Dependencies:     - React; shared illustration primitives.
 * Architecture:     - Presentation-only SVG. No state, no data, no side effects.
 * Notes:            - Update this illustration in the same PR whenever the canvas shell changes.
 */

import React from 'react';
import { CalloutBadge, IllustrationFigure, ILLUS } from './illustrationPrimitives';

/**
 * @component IllustrationBuilderShell
 * Purpose: Annotated diagram of the complete builder shell and its three zones.
 */
export function IllustrationBuilderShell() {
  return (
    <IllustrationFigure
      title="The document builder shell: left panel, canvas, and preview"
      viewBox="0 0 400 240"
      legend={[
        'Header bar: Body / Cover Page tabs and the Add component button.',
        'Left panel: page setup, header, section list, and branding.',
        'Canvas: the page where you add and order bands.',
        'Live preview: exactly what the generated PDF will look like.',
      ]}
    >
      {/* Outer frame */}
      <rect x={2} y={2} width={396} height={236} rx={8} fill={ILLUS.white} stroke={ILLUS.gray300} />
      {/* Header bar */}
      <rect x={2} y={2} width={396} height={26} rx={8} fill={ILLUS.gray100} />
      <rect x={10} y={9} width={36} height={13} rx={3} fill={ILLUS.white} stroke={ILLUS.gray300} />
      <text x={28} y={18} textAnchor="middle" fontSize={8} fill={ILLUS.gray800}>Body</text>
      <rect x={50} y={9} width={52} height={13} rx={3} fill={ILLUS.gray100} stroke={ILLUS.gray300} />
      <text x={76} y={18} textAnchor="middle" fontSize={8} fill={ILLUS.gray500}>Cover Page</text>
      <rect x={318} y={8} width={72} height={15} rx={3} fill={ILLUS.orange} />
      <text x={354} y={18} textAnchor="middle" fontSize={8} fontWeight={600} fill={ILLUS.white}>+ Add component</text>

      {/* Left panel */}
      <rect x={10} y={36} width={96} height={196} rx={5} fill={ILLUS.white} stroke={ILLUS.gray200} />
      {['Page Setup', 'Header', 'Sections', 'Branding'].map((label, i) => (
        <g key={label}>
          <rect x={16} y={44 + i * 20} width={84} height={15} rx={3} fill={ILLUS.gray100} />
          <text x={22} y={54 + i * 20} fontSize={7.5} fill={ILLUS.gray700}>{label}</text>
          <text x={94} y={54 + i * 20} fontSize={8} fill={ILLUS.gray400}>{'\u25BE'}</text>
        </g>
      ))}

      {/* Canvas (page) */}
      <rect x={116} y={36} width={170} height={196} rx={5} fill={ILLUS.gray100} stroke={ILLUS.gray200} />
      <rect x={132} y={46} width={138} height={176} rx={3} fill={ILLUS.white} stroke={ILLUS.gray300} />
      {/* sample band card */}
      <rect x={140} y={56} width={122} height={30} rx={4} fill={ILLUS.white} stroke={ILLUS.orange} strokeWidth={1.5} />
      <rect x={146} y={64} width={6} height={14} rx={1} fill={ILLUS.gray300} />
      <text x={158} y={74} fontSize={8} fontWeight={600} fill={ILLUS.gray800}>Letterhead</text>
      <rect x={140} y={92} width={122} height={24} rx={4} fill={ILLUS.white} stroke={ILLUS.gray300} />
      <text x={158} y={107} fontSize={8} fill={ILLUS.gray700}>Recipient block</text>
      <rect x={140} y={122} width={122} height={24} rx={4} fill={ILLUS.white} stroke={ILLUS.gray300} />
      <text x={158} y={137} fontSize={8} fill={ILLUS.gray700}>Body text</text>

      {/* Preview pane */}
      <rect x={296} y={36} width={94} height={196} rx={5} fill={ILLUS.gray100} stroke={ILLUS.gray200} />
      <rect x={304} y={44} width={78} height={180} rx={3} fill={ILLUS.white} stroke={ILLUS.gray300} />
      <rect x={310} y={50} width={66} height={8} rx={2} fill={ILLUS.gray200} />
      <rect x={310} y={62} width={48} height={6} rx={2} fill={ILLUS.gray200} />
      <rect x={310} y={72} width={66} height={6} rx={2} fill={ILLUS.gray100} />
      <rect x={310} y={82} width={60} height={6} rx={2} fill={ILLUS.gray100} />

      {/* Callouts */}
      <CalloutBadge n={1} x={300} y={15} />
      <CalloutBadge n={2} x={20} y={44} />
      <CalloutBadge n={3} x={126} y={50} />
      <CalloutBadge n={4} x={386} y={44} />
    </IllustrationFigure>
  );
}
